import { useEffect, useMemo, useRef, useState } from "react"
import { useTonConnectUI } from "@tonconnect/ui-react"
import { ArrowUp, ArrowDown } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"

import { DevMenuSheet, PROFILE_DEV_MENU_TAP_COUNT } from "@/features/dev-menu"
import { RoundDetailSheet } from "@/features/history"
import { LanguageSheet } from "@/features/locale/LanguageSheet"
import { DepositSheet, WithdrawSheet, useTonDeposit } from "@/features/wallet"

import { useBalance } from "@/entities/user"
import { usePaymentHistory, paymentHistoryKeys } from "@/entities/history"

import type { PaymentHistoryItem } from "@/shared/api/types"
import { useI18n } from "@/shared/i18n"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"
import {
  useWalletBalance,
  walletBalanceQueryKey,
} from "@/shared/hooks/useWalletBalance"
import { formatBalance, shortenAddress, cn } from "@/shared/lib/utils"
import { useTelegramUser } from "@/shared/lib/useTelegramUser"
import Button from "@/shared/ui/Button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

import { resolveAvatarLoadUrl } from "@/shared/lib/avatarProxy"
import { getAvatarInitials } from "@/shared/lib/avatarInitials"
import { useRapidTapUnlock } from "@/shared/lib/useRapidTapUnlock"

import TonIcon from "@/assets/icons/ton-icon.svg?react"
import DisconnectIcon from "@/assets/icons/disconnect-icon.svg?react"
import LanguagePickerIcon from "@/assets/icons/language-picker.svg?react"

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (ms: number): string => {
  const d = new Date(ms)
  const dd = String(d.getDate()).padStart(2, "0")
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, "0")
  const min = String(d.getMinutes()).padStart(2, "0")
  return `${dd}.${mm}.${yyyy}, ${hh}:${min}`
}

// Wallet icon shown in the connected wallet bar
const WalletIcon = () => (
  <svg
    width="16"
    height="15"
    viewBox="0 0 13 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 10V10.6667C12 11.0203 11.8595 11.3594 11.6095 11.6095C11.3594 11.8595 11.0203 12 10.6667 12H1.33333C0.979711 12 0.640573 11.8595 0.390524 11.6095C0.140476 11.3594 0 11.0203 0 10.6667V1.33333C0 0.979711 0.140476 0.640573 0.390524 0.390524C0.640573 0.140476 0.979711 0 1.33333 0H10.6667C11.0203 0 11.3594 0.140476 11.6095 0.390524C11.8595 0.640573 12 0.979711 12 1.33333V2H6C5.64638 2 5.30724 2.14048 5.05719 2.39052C4.80714 2.64057 4.66667 2.97971 4.66667 3.33333V8.66667C4.66667 9.02029 4.80714 9.35943 5.05719 9.60948C5.30724 9.85952 5.64638 10 6 10M6 8.66667H12.6667V3.33333H6M8.66667 7C8.40145 7 8.1471 6.89464 7.95956 6.70711C7.77202 6.51957 7.66667 6.26522 7.66667 6C7.66667 5.73478 7.77202 5.48043 7.95956 5.29289C8.1471 5.10536 8.40145 5 8.66667 5C8.93188 5 9.18624 5.10536 9.37377 5.29289C9.56131 5.48043 9.66667 5.73478 9.66667 6C9.66667 6.26522 9.56131 6.51957 9.37377 6.70711C9.18624 6.89464 8.93188 7 8.66667 7Z"
      fill="white"
      fillOpacity="0.6"
    />
  </svg>
)

// ─── Activity Item ─────────────────────────────────────────────────────────────

type ActivityVariant =
  | "ton-deposit"
  | "ton-withdrawal"
  | "gift-deposit"
  | "gift-withdrawal"
  | "gift-fee"

interface ActivityRow {
  key: string
  variant: ActivityVariant
  createdAt: number
  amountNano: number | null
  imageUrl: string | null
}

function buildActivityRows(items: PaymentHistoryItem[]): ActivityRow[] {
  const rows: ActivityRow[] = []
  for (const item of items) {
    if (item.currency === "TELEGRAM_GIFT") {
      rows.push({
        key: item.id,
        variant: item.type === "deposit" ? "gift-deposit" : "gift-withdrawal",
        createdAt: item.created_at,
        amountNano: null,
        imageUrl: item.small_image_url ?? null,
      })
      if (
        item.type === "withdrawal" &&
        typeof item.fee_amount === "number" &&
        item.fee_amount > 0
      ) {
        rows.push({
          key: `${item.id}:fee`,
          variant: "gift-fee",
          createdAt: item.created_at,
          amountNano: item.fee_amount,
          imageUrl: null,
        })
      }
      continue
    }
    rows.push({
      key: item.id,
      variant: item.type === "deposit" ? "ton-deposit" : "ton-withdrawal",
      createdAt: item.created_at,
      amountNano: item.amount ?? null,
      imageUrl: null,
    })
  }
  return rows
}

function formatTonNanoSigned(amountNano: number | null, sign: "+" | "-"): string {
  const text = formatTonFromNano(amountNano, {
    placeholder: "—",
    allowZero: true,
  })
  return `${sign}${text} TON`
}

const ActivityItem = ({ row }: { row: ActivityRow }) => {
  const { t } = useI18n()

  const isIncoming =
    row.variant === "ton-deposit" || row.variant === "gift-deposit"
  const isGiftWithImage =
    row.variant === "gift-deposit" || row.variant === "gift-withdrawal"
  const sign: "+" | "-" = isIncoming ? "+" : "-"

  const label =
    row.variant === "ton-deposit"
      ? t("profile.depositItem")
      : row.variant === "ton-withdrawal"
        ? t("profile.withdrawalItem")
        : row.variant === "gift-deposit"
          ? t("profile.giftDepositItem")
          : row.variant === "gift-withdrawal"
            ? t("profile.giftWithdrawalItem")
            : t("profile.giftWithdrawalFee")

  return (
    <div className="flex min-h-[64px] items-center gap-2 px-4 py-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary-main">
        {isIncoming ? (
          <ArrowDown
            className="size-5 text-white"
            strokeWidth={2}
            aria-hidden
          />
        ) : (
          <ArrowUp
            className="size-5 text-white"
            strokeWidth={2}
            aria-hidden
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        <p className="truncate text-base/none font-medium leading-[19px] tracking-[-0.5px] text-white">
          {label}
        </p>
        <p className="text-[10px]/none font-normal tracking-[-0.5px] text-white/60">
          {formatDate(row.createdAt)}
        </p>
      </div>

      {isGiftWithImage ? (
        <div
          className={cn(
            "aspect-square h-10 shrink-0 rounded-lg bg-cover bg-center",
            !row.imageUrl && "bg-secondary-main"
          )}
          style={
            row.imageUrl ? { backgroundImage: `url(${row.imageUrl})` } : undefined
          }
          aria-hidden
        />
      ) : (
        <p
          className={cn(
            "shrink-0 text-base/none font-medium tabular-nums tracking-[-0.5px] text-white",
            isIncoming && "text-success-100"
          )}
        >
          {formatTonNanoSigned(row.amountNano, sign)}
        </p>
      )}
    </div>
  )
}

const ActivityItemSkeleton = () => {
  return (
    <div className="flex min-h-[64px] animate-pulse items-center px-4 py-3">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="size-10 shrink-0 rounded-full bg-[#4A4D54]" />
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
          <div className="h-4 w-28 rounded-md bg-white/[0.14]" />
          <div className="h-3 w-24 rounded-md bg-white/[0.1]" />
        </div>
      </div>
      <div className="flex min-w-0 flex-1 justify-end">
        <div className="h-4 w-20 rounded-md bg-white/[0.14]" />
      </div>
    </div>
  )
}

// ─── Profile Page ──────────────────────────────────────────────────────────────

const ProfilePage = () => {
  const { t } = useI18n()

  const tgUser = useTelegramUser()

  const { data: balance } = useBalance()

  const {
    data: paymentData,
    isLoading: isPaymentLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = usePaymentHistory()

  const queryClient = useQueryClient()

  const [tonConnectUI] = useTonConnectUI()

  const [isDepositOpen, setIsDepositOpen] = useState(false)
  const [showWithdraw, setShowWithdraw] = useState(false)
  const [languageSheetOpen, setLanguageSheetOpen] = useState(false)
  const [devMenuOpen, setDevMenuOpen] = useState(false)

  const registerProfileAvatarTap = useRapidTapUnlock({
    requiredTaps: PROFILE_DEV_MENU_TAP_COUNT,
    onUnlock: () => setDevMenuOpen(true),
  })

  const { connectedAddress: address } = useTonDeposit({
    onSuccess: () => setIsDepositOpen(false),
  })

  const { data: walletBalance } = useWalletBalance(address || null)
  // Kept for RoundDetailSheet — round history is hidden from UI but components remain
  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const displayName =
    tgUser.firstName || tgUser.username || t("profile.playerFallback")
  const balanceAmount = balance?.balance?.amount || "0"
  const activityRows = useMemo(() => {
    const items = paymentData?.pages.flatMap((p) => p.items) ?? []
    return buildActivityRows(items)
  }, [paymentData])
  const showActivitySection = isPaymentLoading || activityRows.length > 0

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage)
          fetchNextPage()
      },
      { threshold: 0.1 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className="min-h-full">
      {/* ── TopBar: 56px rails; language is a single 56×56 button, p-4 for 24px glyph (Figma iconButton) ── */}
      <div className="flex h-14 items-center justify-between px-4">
        <div className="size-14 shrink-0" aria-hidden />
        <div className="flex min-w-0 flex-1 items-center justify-center gap-1 px-1">
          <button
            type="button"
            className="size-6 shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            aria-label={displayName}
            onClick={registerProfileAvatarTap}
          >
            <Avatar className="size-6">
              <AvatarImage
                src={
                  tgUser.photoUrl
                    ? resolveAvatarLoadUrl(tgUser.photoUrl)
                    : undefined
                }
                alt=""
              />
              <AvatarFallback className="bg-secondary-main text-[10px] font-medium text-white">
                {getAvatarInitials(displayName)}
              </AvatarFallback>
            </Avatar>
          </button>
          <p className="min-w-0 truncate text-[16px] font-medium leading-[130%] tracking-[-0.25px] text-[#F5F5F5]">
            {displayName}
          </p>
        </div>
        <button
          type="button"
          className="flex size-14 shrink-0 items-center justify-center p-4 text-white transition-opacity hover:opacity-90 active:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
          aria-label={t("profile.openLanguage")}
          aria-expanded={languageSheetOpen}
          aria-haspopup="dialog"
          onClick={() => setLanguageSheetOpen(true)}
        >
          <LanguagePickerIcon className="size-6 shrink-0" aria-hidden />
        </button>
      </div>

      {/* ── UserCard + Wallet bar ── */}
      <div className="mt-2 px-4">
        {/* UserCard — z-10 so it visually sits on top of the wallet bar */}
        <div className="relative z-10 -mb-4">
          <div className="rounded-3xl bg-secondary-main">
            {/* Balance section */}
            <div className="flex flex-col gap-1 px-6 pt-6">
              <p className="text-sm/none font-normal tracking-[-0.5px] text-white">
                {t("profile.trixBalance")}
              </p>
              <p className="flex items-center gap-1.5 text-[32px]/[32px] font-bold tracking-[-1.5px] text-white">
                {formatBalance(balanceAmount)}
                <TonIcon className="size-8" />
              </p>
            </div>
            {/* Withdraw / Deposit — equal width at any viewport (grid + w-full); px matches Secondary M */}
            <div className="grid grid-cols-2 gap-2 px-4 pb-4 pt-6">
              <Button
                appearance="tertiary"
                size="small"
                onClick={() => setShowWithdraw(true)}
              >
                {t("profile.withdraw")}
              </Button>
              <Button
                appearance="green"
                size="small"
                onClick={() => setIsDepositOpen(true)}
              >
                {t("profile.deposit")}
              </Button>
            </div>
          </div>
        </div>

        {/* Wallet bar — sits behind UserCard, blue when connected / white when not.
            Total height: pt-6 (24px hidden under overlap) + address row (57px) = 81px.
            Visible: 81px − 16px (−mb-4 overlap) = 65px — matches Figma wallet element height. */}
        <div className="relative z-0">
          <div
            className={cn(
              "rounded-b-[32px] pt-4",
              address ? "bg-gray-100" : "bg-green-100"
            )}
          >
            {address ? (
              <div className="flex h-[57px] items-center gap-3 px-6">
                <WalletIcon />
                <span className="flex-1 truncate text-sm/none font-medium tracking-[-0.5px] text-white">
                  {shortenAddress(address, 4)}
                  {walletBalance != null && (
                    <span className="ml-2 font-normal text-white/60">
                      {walletBalance.ton.toFixed(2)} {t("common.ton")}
                    </span>
                  )}
                </span>
                <button
                  onClick={() => tonConnectUI.disconnect()}
                  className="flex h-8 w-8 items-center justify-center text-white/60 transition-colors hover:text-white/70"
                  aria-label={t("common.disconnectWallet")}
                >
                  <DisconnectIcon className="size-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => tonConnectUI.openModal()}
                className="flex h-[57px] w-full items-center justify-center px-6 text-sm/none font-medium tracking-[-0.25px] text-black-100 transition-opacity active:opacity-70"
              >
                {t("common.connectWallet")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Activity ── */}
      {showActivitySection && (
        <div className="mt-6">
          <h2 className="mb-1 flex items-center px-4 text-[24px] font-semibold leading-[29px] tracking-[-0.5px] text-white">
            {t("profile.activity")}
          </h2>
          <div className="flex flex-col items-stretch p-0">
            {isPaymentLoading && activityRows.length === 0
              ? [1, 2, 3].map((skeletonId) => (
                  <ActivityItemSkeleton key={skeletonId} />
                ))
              : activityRows.map((row) => (
                  <ActivityItem key={row.key} row={row} />
                ))}
            {isFetchingNextPage &&
              [1, 2].map((skeletonId) => (
                <ActivityItemSkeleton key={`next-${skeletonId}`} />
              ))}
          </div>
        </div>
      )}

      <div ref={sentinelRef} className="h-4" />

      {/* ── Deposit sheet (Vaul drawer — renders above tab bar via portal) ── */}
      <DepositSheet
        isOpen={isDepositOpen}
        setIsOpen={setIsDepositOpen}
        depositUiSource="profile"
      />

      <WithdrawSheet
        open={showWithdraw}
        onClose={() => setShowWithdraw(false)}
        balance={balanceAmount}
        address={address}
        walletBalance={walletBalance?.ton ?? null}
        onDisconnect={() => {
          tonConnectUI.disconnect()
          setShowWithdraw(false)
        }}
        onSuccess={() => {
          void queryClient.invalidateQueries({
            queryKey: paymentHistoryKeys.infinite(),
          })
          if (address) {
            void queryClient.invalidateQueries({
              queryKey: walletBalanceQueryKey(address),
            })
          }
        }}
      />

      {/* RoundDetailSheet kept for future use; not triggered from this page currently */}
      <RoundDetailSheet
        roundId={selectedRoundId}
        onClose={() => setSelectedRoundId(null)}
      />

      <LanguageSheet
        open={languageSheetOpen}
        onClose={() => setLanguageSheetOpen(false)}
      />

      <DevMenuSheet
        open={devMenuOpen}
        onClose={() => setDevMenuOpen(false)}
      />
    </div>
  )
}

export default ProfilePage
